import { DEFAULT_LANGUAGE, SHIPPED_LANGUAGE_CODES, isShippedLanguageCode } from './languages';

/**
 * Which language to show, from the three places a preference can come from.
 *
 * Pure so it can be tested without a DOM, and so the inline script in
 * public/index.html can mirror it exactly.
 *
 * KEEP IN SYNC with that script — it runs before this module loads, and a
 * disagreement shows up as a flash of the wrong direction on first paint.
 * src/prepaint.test.js fails if the two drift apart.
 *
 * Resolution only ever lands on a SHIPPED language: an unshipped code (from
 * the URL, storage, or the browser) has no locale file, so honoring it would
 * stamp e.g. lang="ja" on the document while silently rendering English
 * content underneath — wrong for screen readers and font selection, and a
 * broken promise to search crawlers reading hreflang.
 */

// Codes the browser reports that are not codes we ship, but that have an
// obvious home. Chinese is the only real case: we ship one script.
const ALIASES = { zh: 'zh-Hans', 'zh-cn': 'zh-Hans', 'zh-sg': 'zh-Hans', 'zh-tw': 'zh-Hans', 'zh-hk': 'zh-Hans' };

/**
 * The negotiation algorithm (base-subtag matching, zh aliasing,
 * case-insensitivity, first-supported-wins) takes the candidate code list as
 * a parameter, defaulted to the real shipped set. That keeps the algorithm
 * testable on its own terms — e.g. "first supported tag wins" needs at least
 * two candidates to mean anything, which today's two-language shipped set
 * can't exercise — without the tests reaching around the module to fake the
 * registry.
 */
function negotiateOne(tag, codes) {
  if (typeof tag !== 'string' || tag === '') return null;
  const lower = tag.toLowerCase();
  const byLower = new Map(codes.map((code) => [code.toLowerCase(), code]));

  if (byLower.has(lower)) return byLower.get(lower);
  if (ALIASES[lower] && byLower.has(ALIASES[lower].toLowerCase())) return ALIASES[lower];

  const base = lower.split('-')[0];
  if (byLower.has(base)) return byLower.get(base);
  if (ALIASES[base] && byLower.has(ALIASES[base].toLowerCase())) return ALIASES[base];

  return null;
}

export function negotiate(languages = [], codes = SHIPPED_LANGUAGE_CODES) {
  for (const tag of languages) {
    const match = negotiateOne(tag, codes);
    if (match) return match;
  }
  return null;
}

export function resolveLanguage({ search = '', stored = null, languages = [] } = {}) {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (isShippedLanguageCode(fromUrl)) return fromUrl;
  if (isShippedLanguageCode(stored)) return stored;
  return negotiate(languages) || DEFAULT_LANGUAGE;
}
